import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import HttpClient from "../../lib/HttpClient";
import { getPortalAuthConfig } from "../../portals/portalConfig";
import CaProfile from "../../models/domain/CaProfile";
import { useSnackbarContext } from "../../contexts/Snackbar";

const ENQUIRY_CATEGORIES = [
  "I need help using the portal",
  "I have a suggestion",
  "I would like to offer this to other doctors",
  "Something else",
] as const;

const MAX_MESSAGE_LENGTH = 500;
const CATEGORY_PLACEHOLDER = "";

function defaultContactValue(profile: CaProfile): string {
  const parts = [profile.email, profile.mobile].filter((v) => v?.trim());
  return parts.join(" / ");
}

interface FieldErrors {
  name?: string;
  contact?: string;
  category?: string;
  message?: string;
  submit?: string;
}

interface Props {
  show: boolean;
  onClose: () => void;
  profile: CaProfile;
  selectedClinicId: string;
}

function validateFields(
  name: string,
  contact: string,
  category: string,
  message: string,
): FieldErrors {
  const errors: FieldErrors = {};
  if (!name.trim()) {
    errors.name = "Please enter your name.";
  }
  if (!contact.trim()) {
    errors.contact = "Please enter an email address or phone number.";
  }
  if (!category) {
    errors.category = "Please select an option.";
  }
  if (!message.trim()) {
    errors.message = "Please enter your message.";
  }
  return errors;
}

export default function CaContactUsModal({
  show,
  onClose,
  profile,
  selectedClinicId,
}: Props) {
  const { setSnackbar } = useSnackbarContext();
  const [name, setName] = useState(profile.name);
  const [contact, setContact] = useState(() => defaultContactValue(profile));
  const [category, setCategory] = useState(CATEGORY_PLACEHOLDER);
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  useEffect(() => {
    if (show) {
      setName(profile.name);
      setContact(defaultContactValue(profile));
      setCategory(CATEGORY_PLACEHOLDER);
      setMessage("");
      setFieldErrors({});
    }
  }, [show, profile]);

  const clearFieldError = (field: keyof FieldErrors) => {
    setFieldErrors((prev) => {
      if (!prev[field]) {
        return prev;
      }
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleSend = async () => {
    const validationErrors = validateFields(name, contact, category, message);
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      return;
    }

    const contactUrl = getPortalAuthConfig().contactEnquiryUrl;
    if (!contactUrl) {
      return;
    }

    setFieldErrors({});
    setIsSubmitting(true);
    try {
      const response = await HttpClient.performPost<{
        status?: { success: boolean; message?: string };
      }>(contactUrl, {
        name: name.trim(),
        contact: contact.trim(),
        category,
        message: message.trim(),
        clinicId: selectedClinicId,
      });

      if (!response.body?.status?.success) {
        setFieldErrors({
          submit:
            response.body?.status?.message ??
            "Could not send your enquiry. Please try again.",
        });
      } else {
        setSnackbar({
          message: "Your message has been sent.",
          severity: "success",
        });
        onClose();
      }
    } catch {
      setFieldErrors({
        submit: "Could not send your enquiry. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      className="ca-contact-modal"
      backdrop="static"
    >
      <Modal.Header closeButton={false}>
        <Modal.Title>Contact Us</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form noValidate>
          {fieldErrors.submit && (
            <div
              className="alert alert-danger py-2 small mb-3"
              role="alert"
            >
              {fieldErrors.submit}
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              value={name}
              isInvalid={Boolean(fieldErrors.name)}
              onChange={(e) => {
                setName(e.target.value);
                clearFieldError("name");
                clearFieldError("submit");
              }}
            />
            <Form.Control.Feedback type="invalid">
              {fieldErrors.name}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Email address / Phone number</Form.Label>
            <Form.Control
              type="text"
              value={contact}
              isInvalid={Boolean(fieldErrors.contact)}
              onChange={(e) => {
                setContact(e.target.value);
                clearFieldError("contact");
                clearFieldError("submit");
              }}
            />
            <Form.Control.Feedback type="invalid">
              {fieldErrors.contact}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              What can we help you with?{" "}
              <span className="ca-contact-modal__required">*</span>
            </Form.Label>
            <Form.Select
              value={category}
              isInvalid={Boolean(fieldErrors.category)}
              onChange={(e) => {
                setCategory(e.target.value);
                clearFieldError("category");
                clearFieldError("submit");
              }}
              className={!category ? "ca-contact-modal__select-placeholder" : ""}
            >
              <option value="" disabled>
                Select an option
              </option>
              {ENQUIRY_CATEGORIES.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {fieldErrors.category}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-1">
            <Form.Label>Your message</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              maxLength={MAX_MESSAGE_LENGTH}
              placeholder="Type your message here..."
              value={message}
              isInvalid={Boolean(fieldErrors.message)}
              onChange={(e) => {
                setMessage(e.target.value);
                clearFieldError("message");
                clearFieldError("submit");
              }}
            />
            <Form.Control.Feedback type="invalid">
              {fieldErrors.message}
            </Form.Control.Feedback>
          </Form.Group>
          <div className="ca-contact-modal__char-count text-muted small text-end">
            {message.length} / {MAX_MESSAGE_LENGTH}
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outline-primary"
          className="ca-contact-modal__btn-cancel"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          className="ca-contact-modal__btn-send"
          onClick={handleSend}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Sending..." : "Send"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
